import { LitElement, html, css } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";

/**
 * Modal for importing a comparison from a URL or hash.
 * @fires close - When the modal is dismissed.
 * @fires import - When a URL is submitted (detail: { url: string }).
 */
@customElement("bab-import-modal")
export class BabImportModal extends LitElement {
  static styles = css`
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(2px);
      z-index: 2000;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s ease;
    }

    .modal-overlay--visible {
      opacity: 1;
      visibility: visible;
    }

    .modal-content {
      width: 90%;
      max-width: 500px;
      background: var(--bab-card-bg);
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: var(--bab-radius, 12px);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
      padding: 0;
      overflow: hidden;
      transform: scale(0.95) translateY(20px);
      transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .modal-overlay--visible .modal-content {
      transform: scale(1) translateY(0);
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      border-bottom: var(--bab-border-width, 1.5px) solid var(--bab-border);
    }

    .modal-header h3 {
      margin: 0;
      font-size: 1.1rem;
      color: var(--bab-text);
    }

    .modal-close {
      background: transparent;
      border: none;
      color: var(--bab-text-secondary);
      font-size: 1.25rem;
      cursor: pointer;
      padding: 4px;
      line-height: 1;
      transition: color 0.2s;
    }

    .modal-close:hover {
      color: var(--bab-text);
    }

    .modal-body {
      padding: 20px;
    }

    .modal-body p {
      margin: 0 0 12px 0;
      font-size: 0.9rem;
      color: var(--bab-text-secondary);
      line-height: 1.5;
    }

    .modal-input {
      width: 100%;
      padding: 10px 14px;
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: var(--bab-radius, 12px);
      background: var(--bab-input-bg, #fff);
      color: var(--bab-input-text, #000);
      font-size: 0.95rem;
      font-family: inherit;
      box-sizing: border-box;
      transition: border-color 0.2s;
    }

    .modal-input:focus {
      outline: none;
      border-color: var(--bab-accent, #4a90e2);
    }

    .modal-footer {
      padding: 16px 20px;
      background: var(--bab-bg, #f0f4f8);
      border-top: var(--bab-border-width, 1.5px) solid var(--bab-border);
      display: flex;
      justify-content: flex-end;
      gap: 12px;
    }

    .btn {
      padding: 9px 20px;
      background: var(--bab-accent, #4a90e2);
      color: #fff;
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: var(--bab-radius, 12px);
      font-size: 0.9rem;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: opacity 0.15s, transform 0.1s;
    }

    .btn:hover:not(:disabled) {
      opacity: 0.88;
    }

    .btn:active:not(:disabled) {
      transform: scale(0.97);
    }

    .btn--secondary {
      background: var(--bab-button-secondary-bg, #fff);
      color: var(--bab-button-secondary-text, #4a90e2);
    }

    .btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `;

  @property({ type: Boolean })
  open = false;

  @state()
  private _url = "";

  @query(".modal-input")
  private _input!: HTMLInputElement;

  private _onKeyDown = (e: KeyboardEvent) => {
    if (!this.open) return;

    if (e.key === "Escape") {
      this._close();
      return;
    }

    if (e.key === "Tab") {
      const focusable = Array.from(
        this.shadowRoot!.querySelectorAll<HTMLElement>("button:not(:disabled), input:not(:disabled)")
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = this.shadowRoot!.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("keydown", this._onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("keydown", this._onKeyDown);
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has("open") && this.open) {
      this._url = "";
      setTimeout(() => this._input?.focus(), 50);
    }
  }

  private _close() {
    this.dispatchEvent(new CustomEvent("close"));
  }

  private _handleImport() {
    const url = this._url.trim();
    if (url) {
      this.dispatchEvent(new CustomEvent("import", { detail: { url } }));
    }
  }

  render() {
    return html`
      <div class="modal-overlay ${this.open ? "modal-overlay--visible" : ""}"
           aria-hidden="${!this.open}"
           @click="${this._close}">
        <div class="modal-content"
             role="dialog"
             aria-modal="true"
             aria-labelledby="modal-title"
             @click="${(e: Event) => e.stopPropagation()}">
          <div class="modal-header">
            <h3 id="modal-title">Import Comparison</h3>
            <button class="modal-close" aria-label="Close" @click="${this._close}">✕</button>
          </div>
          <div class="modal-body">
            <p>Paste a BAB Chart URL or hash to import the data as a new comparison.</p>
            <input 
              type="text" 
              class="modal-input" 
              placeholder="https://babchart.app/#v1:..." 
              .value="${this._url}"
              @input="${(e: Event) => this._url = (e.target as HTMLInputElement).value}"
              @keydown="${(e: KeyboardEvent) => e.key === "Enter" && this._handleImport()}"
            />
          </div>
          <div class="modal-footer">
            <button class="btn btn--secondary" @click="${this._close}">Cancel</button>
            <button class="btn" @click="${this._handleImport}" ?disabled="${!this._url.trim()}">Import</button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bab-import-modal": BabImportModal;
  }
}
